import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Database, MapPin, Search, ShoppingCart, Unlock, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL, HAS_API_URL } from '../config/api';
import { isAdminEmail } from '../lib/userLimits';
import { trackEvent } from '../utils/analytics';

const SHOP_COUNTRY_STORAGE_KEY = 'myroomsai_shop_country';
const SHOP_COUNTRY_MANUAL_STORAGE_KEY = 'myroomsai_shop_country_manual';
const POLL_MS = 2500;
const PRODUCT_SEARCH_CREDIT_COST = 2;

export const COUNTRY_OPTIONS = [
  { label: 'United Arab Emirates', value: 'ae', serpCountry: 'ae' },
  { label: 'Egypt', value: 'eg', serpCountry: 'eg' },
  { label: 'Saudi Arabia', value: 'sa', serpCountry: 'sa' },
  { label: 'Qatar', value: 'qa', serpCountry: 'qa' },
  { label: 'Kuwait', value: 'kw', serpCountry: 'kw' },
  { label: 'Bahrain', value: 'bh', serpCountry: 'bh' },
  { label: 'Oman', value: 'om', serpCountry: 'om' },
  { label: 'United States', value: 'us', serpCountry: 'us' },
  { label: 'United Kingdom', value: 'uk', serpCountry: 'uk' },
  { label: 'Germany', value: 'de', serpCountry: 'de' },
  { label: 'France', value: 'fr', serpCountry: 'fr' },
  { label: 'Italy', value: 'it', serpCountry: 'it' },
  { label: 'Spain', value: 'es', serpCountry: 'es' },
  { label: 'Netherlands', value: 'nl', serpCountry: 'nl' },
  { label: 'Canada', value: 'ca', serpCountry: 'ca' },
  { label: 'Australia', value: 'au', serpCountry: 'au' },
];

const COUNTRY_CODE_ALIASES = {
  gb: 'uk',
};

const COUNTRY_SHORT_LABELS = {
  ae: 'UAE',
  eg: 'EG',
  sa: 'KSA',
  qa: 'QA',
  kw: 'KW',
  bh: 'BH',
  om: 'OM',
  us: 'USA',
  uk: 'UK',
  de: 'DE',
  fr: 'FR',
  it: 'IT',
  es: 'ES',
  nl: 'NL',
  ca: 'CA',
  au: 'AU',
};

const IMAGE_WRAPPER_STYLE = {
  position: 'relative',
  display: 'inline-block',
  lineHeight: 0,
  maxWidth: '100%',
};

const IMAGE_STYLE = {
  display: 'block',
  maxWidth: '100%',
  height: 'auto',
  borderRadius: 12,
  boxShadow: '0 6px 24px rgba(0,0,0,0.18)',
};

function countryFromBrowserLocale() {
  if (typeof navigator === 'undefined') return '';
  const locale = navigator.language || (Array.isArray(navigator.languages) ? navigator.languages[0] : '');
  const region = typeof locale === 'string' && locale.includes('-') ? locale.split('-').pop().toLowerCase() : '';
  const map = {
    us: 'us',
    ae: 'ae',
    eg: 'eg',
    gb: 'uk',
    uk: 'uk',
    sa: 'sa',
    qa: 'qa',
    kw: 'kw',
    bh: 'bh',
    om: 'om',
    de: 'de',
    fr: 'fr',
    it: 'it',
    es: 'es',
    nl: 'nl',
    ca: 'ca',
    au: 'au',
  };
  return map[region] || '';
}

function normalizeShoppingCountry(country) {
  const value = typeof country === 'string' ? country.trim().toLowerCase() : '';
  if (!value) return '';
  const key = COUNTRY_CODE_ALIASES[value] || value;
  return COUNTRY_OPTIONS.some((option) => option.value === key) ? key : '';
}

function countryForProductRequest(country) {
  return normalizeShoppingCountry(country);
}

function readStoredCountry() {
  try {
    if (localStorage.getItem(SHOP_COUNTRY_MANUAL_STORAGE_KEY) !== '1') return '';
    const v = localStorage.getItem(SHOP_COUNTRY_STORAGE_KEY);
    const country = normalizeShoppingCountry(v);
    if (country) return country;
  } catch {
    /* ignore */
  }
  return '';
}

function fallbackShoppingCountry() {
  return countryFromBrowserLocale() || 'ae';
}

function logLocationDebug(message, data) {
  if (process.env.NODE_ENV !== 'development') return;
  console.log(message, data);
}

function itemKey(item, idx) {
  const id =
    typeof item.itemId === 'string' ? item.itemId : typeof item.id === 'string' ? item.id : '';
  return id || `item-${idx}`;
}

function itemIdOf(item) {
  if (typeof item.itemId === 'string' && item.itemId.trim()) return item.itemId.trim();
  if (typeof item.id === 'string' && item.id.trim()) return item.id.trim();
  return '';
}

function itemLabel(item) {
  const label = typeof item?.label === 'string' ? item.label.trim() : '';
  if (label) return label;
  const category = typeof item?.category === 'string' ? item.category.trim() : '';
  return category || 'Detected item';
}

function itemThumbUrl(item) {
  if (typeof item?.cropUrl === 'string' && item.cropUrl.trim()) return item.cropUrl.trim();
  if (typeof item?.cropImageUrl === 'string' && item.cropImageUrl.trim()) return item.cropImageUrl.trim();
  return '';
}

function productTitle(product) {
  const title = typeof product?.title === 'string' ? product.title.trim() : '';
  return title || 'Similar product';
}

function productMeta(product) {
  return [product?.source, product?.price].filter(Boolean).join(' · ');
}

function isSameSelection(a, b) {
  if (!a || !b) return false;
  return itemIdOf(a.item) === itemIdOf(b.item) && a.index === b.index;
}

function productCacheKey(itemId, country) {
  return `${String(itemId || '').trim()}__${String(country || '').trim() || 'all'}`;
}

function countryLabel(country) {
  const key = normalizeShoppingCountry(country);
  return COUNTRY_OPTIONS.find((option) => option.value === key)?.label || 'this location';
}

function countryShortLabel(country) {
  const key = normalizeShoppingCountry(country);
  return COUNTRY_SHORT_LABELS[key] || key.toUpperCase() || 'location';
}

function dotPosition(item, renderedWidth, renderedHeight) {
  const point = item?.point_1000;
  if (point && Number.isFinite(Number(point.x)) && Number.isFinite(Number(point.y))) {
    return {
      left: (Number(point.x) / 1000) * renderedWidth,
      top: (Number(point.y) / 1000) * renderedHeight,
    };
  }
  const bbox = item?.bbox_1000;
  if (!bbox) return null;
  const x1 = Number(bbox.x1);
  const y1 = Number(bbox.y1);
  const x2 = Number(bbox.x2);
  const y2 = Number(bbox.y2);
  if (![x1, y1, x2, y2].every(Number.isFinite)) return null;
  return {
    left: ((x1 + x2) / 2 / 1000) * renderedWidth,
    top: ((y1 + y2) / 2 / 1000) * renderedHeight,
  };
}

function bboxOverlayStyle(item, renderedWidth, renderedHeight) {
  const bbox = item?.bbox_1000;
  if (!bbox) return null;
  const x1 = Number(bbox.x1);
  const y1 = Number(bbox.y1);
  const x2 = Number(bbox.x2);
  const y2 = Number(bbox.y2);
  if (![x1, y1, x2, y2].every(Number.isFinite) || x2 <= x1 || y2 <= y1) return null;
  return {
    left: (x1 / 1000) * renderedWidth,
    top: (y1 / 1000) * renderedHeight,
    width: ((x2 - x1) / 1000) * renderedWidth,
    height: ((y2 - y1) / 1000) * renderedHeight,
  };
}

function cachedSearchesByKey(rows) {
  const out = {};
  if (!Array.isArray(rows)) return out;
  rows.forEach((row) => {
    const itemId = typeof row?.itemId === 'string' ? row.itemId.trim() : '';
    const country = normalizeShoppingCountry(row?.countryCode || row?.country);
    if (!itemId || !country) return;
    const products = Array.isArray(row.products)
      ? row.products
      : Array.isArray(row.results)
        ? row.results
        : [];
    out[productCacheKey(itemId, country)] = {
      ...row,
      country,
      countryCode: country,
      status: typeof row.status === 'string' ? row.status : 'ready',
      products,
      results: products,
      cached: true,
    };
  });
  return out;
}

function locationUnlocksByCountry(rows) {
  const out = {};
  if (!Array.isArray(rows)) return out;
  rows.forEach((row) => {
    const country =
      typeof row?.countryCode === 'string'
        ? normalizeShoppingCountry(row.countryCode)
        : typeof row?.country === 'string'
          ? normalizeShoppingCountry(row.country)
          : '';
    if (!country) return;
    out[country] = {
      ...row,
      countryCode: country,
      country,
      unlocked: row?.unlocked === true,
      cached: true,
    };
  });
  return out;
}

function ShopFurniturePanel({ projectId, generatedImageUrl, onClose }) {
  const { user } = useAuth();
  const imgRef = useRef(null);
  const fetchRequestIdRef = useRef(0);
  const [imgRect, setImgRect] = useState({ width: 0, height: 0 });
  const [prepStatus, setPrepStatus] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedShoppingCountry, setSelectedShoppingCountry] = useState(() => readStoredCountry() || null);
  const [shoppingCountrySource, setShoppingCountrySource] = useState(() =>
    readStoredCountry() ? 'manual' : 'pending',
  );
  const [unlockLoading, setUnlockLoading] = useState(false);
  const [itemSearchLoadingKey, setItemSearchLoadingKey] = useState('');
  const [productSearchError, setProductSearchError] = useState('');
  const [productSearch, setProductSearch] = useState(null);
  const [itemSearchResults, setItemSearchResults] = useState({});
  const [locationUnlocks, setLocationUnlocks] = useState({});
  const [isDesktopLayout, setIsDesktopLayout] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 1024px)').matches : true,
  );

  const adminUser = isAdminEmail(user?.email);
  const showFreeTrialCopy = false;

  const measure = useCallback(() => {
    const node = imgRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    setImgRect((prev) =>
      Math.abs(prev.width - rect.width) < 0.5 && Math.abs(prev.height - rect.height) < 0.5
        ? prev
        : { width: rect.width, height: rect.height },
    );
  }, []);

  const fetchShopItems = useCallback(async () => {
    if (!user || !projectId || !HAS_API_URL) return null;
    const idToken = await user.getIdToken(true);
    const res = await fetch(
      `${API_BASE_URL}/api/projects/${encodeURIComponent(projectId)}/shop-items`,
      {
        headers: { Authorization: `Bearer ${idToken}` },
      },
    );
    const text = await res.text();
    let body = null;
    try {
      body = text ? JSON.parse(text) : null;
    } catch {
      /* ignore */
    }
    if (!res.ok) {
      throw new Error(
        (body && typeof body.error === 'string' && body.error) || `Request failed (${res.status}).`,
      );
    }
    return body;
  }, [user, projectId]);

  const loadShopItems = useCallback(
    async ({ initial = false } = {}) => {
      if (!user || !projectId) return;
      const myRequestId = ++fetchRequestIdRef.current;
      if (initial) {
        setLoading(true);
        setError('');
      }
      try {
        const body = await fetchShopItems();
        if (myRequestId !== fetchRequestIdRef.current) return;
        const status =
          typeof body?.shopPrepStatus === 'string' ? body.shopPrepStatus : 'processing';
        setPrepStatus(status);
        if (status === 'failed') {
          setError(
            typeof body?.error === 'string'
              ? body.error
              : 'Furniture preparation failed. Please try again later.',
          );
          setItems([]);
        } else if (status === 'ready') {
          const nextItems = Array.isArray(body?.items) ? body.items : [];
          const nextUnlocks = locationUnlocksByCountry(body?.shopLocationUnlocks);
          setItems(nextItems);
          setLocationUnlocks(nextUnlocks);
          setItemSearchResults(cachedSearchesByKey(body?.cachedProductSearches));
          setError('');
        } else {
          setItems([]);
        }
      } catch (err) {
        if (myRequestId !== fetchRequestIdRef.current) return;
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (myRequestId === fetchRequestIdRef.current && initial) setLoading(false);
      }
    },
    [user, projectId, fetchShopItems],
  );

  useEffect(() => {
    if (!user || !projectId) return undefined;
    fetchRequestIdRef.current += 1;
    loadShopItems({ initial: true });
    return () => {
      fetchRequestIdRef.current += 1;
    };
  }, [user, projectId, loadShopItems]);

  useEffect(() => {
    if (prepStatus !== 'processing') return undefined;
    const id = window.setInterval(() => {
      loadShopItems({ initial: false });
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [prepStatus, loadShopItems]);

  useEffect(() => {
    function onResize() {
      measure();
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [measure]);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    function onChange() {
      setIsDesktopLayout(mq.matches);
    }
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const storedCountry = readStoredCountry();
    if (storedCountry) {
      setSelectedShoppingCountry(storedCountry);
      setShoppingCountrySource('manual');
    }

    async function detectShoppingCountry() {
      if (!HAS_API_URL) {
        if (!storedCountry && !cancelled) {
          setSelectedShoppingCountry(fallbackShoppingCountry());
          setShoppingCountrySource('fallback');
        }
        return;
      }

      try {
        const res = await fetch(`${API_BASE_URL}/api/location/country`);
        const text = await res.text();
        let body = null;
        try {
          body = text ? JSON.parse(text) : null;
        } catch {
          /* ignore */
        }
        if (!res.ok) throw new Error((body && (body.message || body.error)) || 'Location detection failed');
        const detectedCountry = normalizeShoppingCountry(body?.countryCode);
        const source = body?.source === 'fallback' ? 'fallback' : 'detected';
        logLocationDebug('[Location] detected shopping country', {
          country: detectedCountry || '',
          countryName: body?.countryName || countryLabel(detectedCountry),
        });
        logLocationDebug('[Location] source', { source: body?.source || source });
        if (cancelled || storedCountry) return;
        setSelectedShoppingCountry(detectedCountry || fallbackShoppingCountry());
        setShoppingCountrySource(detectedCountry ? source : 'fallback');
      } catch {
        if (!storedCountry && !cancelled) {
          setSelectedShoppingCountry(fallbackShoppingCountry());
          setShoppingCountrySource('fallback');
        }
      }
    }

    detectShoppingCountry();
    return () => {
      cancelled = true;
    };
  }, []);

  useLayoutEffect(() => {
    measure();
  }, [selectedItem, isDesktopLayout, generatedImageUrl, items, measure]);

  useEffect(() => {
    if (!generatedImageUrl || typeof ResizeObserver === 'undefined') return undefined;
    const node = imgRef.current;
    if (!node) return undefined;
    const ro = new ResizeObserver(() => measure());
    ro.observe(node);
    return () => ro.disconnect();
  }, [generatedImageUrl, measure, items]);

  const selectedItemId = selectedItem ? itemIdOf(selectedItem.item) : '';
  const selectedCountry = selectedShoppingCountry || 'ae';
  const selectedCountryName = countryLabel(selectedCountry);
  const selectedCountryIsManual = shoppingCountrySource === 'manual';
  const shoppingLocationLabel =
    selectedCountryIsManual || shoppingCountrySource === 'manual'
      ? `Shopping location: ${selectedCountryName}`
      : shoppingCountrySource === 'detected'
        ? `Shopping location detected: ${selectedCountryName}`
        : 'Select shopping location';
  const selectedCountryUnlocked = locationUnlocks[selectedCountry]?.unlocked === true;

  useEffect(() => {
    logLocationDebug('[ShopUI] selected country', {
      country: selectedCountry,
      countryName: selectedCountryName,
    });
    logLocationDebug('[ShopUI] location unlocked true/false', {
      country: selectedCountry,
      unlocked: selectedCountryUnlocked,
    });
  }, [selectedCountry, selectedCountryName, selectedCountryUnlocked]);

  useEffect(() => {
    if (!selectedItemId) {
      setProductSearch(null);
      setProductSearchError('');
      return;
    }
    const key = productCacheKey(selectedItemId, selectedCountry);
    if (!selectedCountryUnlocked) {
      setProductSearch(null);
      setProductSearchError('');
      return;
    }
    const cached = itemSearchResults[key];
    setProductSearch(
      cached?.status === 'ready'
        ? {
            itemId: selectedItemId,
            country: selectedCountry,
            products: Array.isArray(cached.products) ? cached.products : [],
            cached: true,
          }
        : null,
    );
    setProductSearchError('');
  }, [selectedItemId, selectedCountry, itemSearchResults, selectedCountryUnlocked]);

  useEffect(() => {
    if (selectedItem || items.length === 0) return;
    setSelectedItem({ item: items[0], index: 0 });
  }, [items, selectedItem]);

  const unlockFurnitureSearch = useCallback(async () => {
    if (!user || !projectId || selectedCountryUnlocked || !HAS_API_URL) return;
    setUnlockLoading(true);
    setProductSearchError('');
    try {
      const idToken = await user.getIdToken(true);
      const requestCountry = countryForProductRequest(selectedCountry);
      logLocationDebug('[ShopUI] unlock clicked', { projectId, country: requestCountry || selectedCountry });
      const res = await fetch(
        `${API_BASE_URL}/api/shop/unlock-location`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            projectId,
            country: requestCountry || selectedCountry,
          }),
        },
      );
      const text = await res.text();
      let body = null;
      try {
        body = text ? JSON.parse(text) : null;
      } catch {
        /* ignore */
      }
      if (!res.ok) {
        setProductSearchError(
          (body && (body.message || body.error)) || `Unlock failed (${res.status}).`,
        );
        return;
      }
      const responseCountry =
        typeof body?.countryCode === 'string' && body.countryCode.trim()
          ? body.countryCode.trim()
          : typeof body?.country === 'string' && body.country.trim()
            ? body.country.trim()
            : selectedCountry;
      const nextUnlock = {
        ...(body?.shopLocationUnlock || {}),
        country: responseCountry,
        countryCode: responseCountry,
        unlocked: true,
        cached: true,
      };
      setLocationUnlocks((prev) => ({ ...prev, [responseCountry]: nextUnlock }));
      setProductSearch(null);
      if (body?.charged === true) {
        trackEvent('furniture_search_unlocked', {
          selectedShoppingCountry: responseCountry,
          credits_spent: 2,
          project_id: projectId,
        });
      }
    } catch (err) {
      setProductSearchError(err instanceof Error ? err.message : String(err));
    } finally {
      setUnlockLoading(false);
    }
  }, [projectId, selectedCountry, selectedCountryUnlocked, user]);

  const searchProductsFor = useCallback(async (selection, { forceRefresh = false } = {}) => {
    const itemId = selection?.item ? itemIdOf(selection.item) : selectedItemId;
    if (!user || !projectId || !itemId) return;
    if (selection) setSelectedItem(selection);
    const itemCountry = selectedShoppingCountry || 'ae';
    const key = productCacheKey(itemId, itemCountry);
    logLocationDebug('[ShopUI] item search clicked', { projectId, itemId, country: itemCountry });
    logLocationDebug('[ShopUI] result key', { key });
    const cached = selectedCountryUnlocked ? itemSearchResults[key] : null;
    if (!forceRefresh && cached?.status === 'ready') {
      setSelectedItem(selection || selectedItem);
      setProductSearch({
        itemId,
        country: itemCountry,
        products: Array.isArray(cached.products) ? cached.products : [],
        cached: true,
      });
      setProductSearchError('');
      const searchedItem = selection?.item || selectedItem?.item;
      if (searchedItem) {
        trackEvent('furniture_item_searched', {
          item_name: itemLabel(searchedItem),
          selectedShoppingCountry: itemCountry,
          project_id: projectId,
        });
      }
      return;
    }
    if (!selectedCountryUnlocked) {
      setProductSearchError(`Furniture search is locked for ${countryLabel(itemCountry)}.`);
      return;
    }
    setItemSearchLoadingKey(key);
    setProductSearchError('');
    if (!forceRefresh) setProductSearch(null);
    try {
      const idToken = await user.getIdToken(true);
      const requestCountry = countryForProductRequest(itemCountry);
      const requestBody = {
        projectId,
        itemId,
        forceRefresh,
        country: requestCountry || itemCountry,
      };
      const res = await fetch(
        `${API_BASE_URL}/api/shop/search-item`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify(requestBody),
        },
      );
      const text = await res.text();
      let body = null;
      try {
        body = text ? JSON.parse(text) : null;
      } catch {
        /* ignore */
      }
      if (!res.ok) {
        if (body?.code === 'FREE_FURNITURE_SEARCH_LIMIT_REACHED') {
          setProductSearchError(
            'You have used your 5 free item searches for this furniture search project.',
          );
        } else {
          setProductSearchError(
            (body && typeof body.error === 'string' && body.error) ||
              `Product search failed (${res.status}).`,
          );
        }
        return;
      }
      const responseCountry =
        typeof body?.countryCode === 'string' && body.countryCode.trim()
          ? body.countryCode.trim()
          : typeof body?.country === 'string' && body.country.trim()
            ? body.country.trim()
            : itemCountry;
      const products = Array.isArray(body?.products)
        ? body.products
        : Array.isArray(body?.results)
          ? body.results
          : [];
      const nextSearch = {
        itemId,
        country: responseCountry,
        countryCode: responseCountry,
        status: body?.status || 'ready',
        products,
        results: products,
        cached: body?.cached === true,
        refreshed: body?.refreshed === true,
      };
      setProductSearch(nextSearch);
      setItemSearchResults((prev) => ({
        ...prev,
        [productCacheKey(itemId, responseCountry)]: {
          ...body,
          itemId,
          country: responseCountry,
          countryCode: responseCountry,
          status: nextSearch.status,
          products: nextSearch.products,
          results: nextSearch.products,
          cached: true,
        },
      }));
      const searchedItem = selection?.item || selectedItem?.item;
      if (searchedItem) {
        trackEvent('furniture_item_searched', {
          item_name: itemLabel(searchedItem),
          selectedShoppingCountry: responseCountry,
          project_id: projectId,
        });
      }
    } catch (err) {
      setProductSearchError(err instanceof Error ? err.message : String(err));
    } finally {
      setItemSearchLoadingKey('');
    }
  }, [
    itemSearchResults,
    projectId,
    selectedItem,
    selectedItemId,
    selectedCountryUnlocked,
    selectedShoppingCountry,
    user,
  ]);

  async function forceRefreshPrep() {
    if (!user || !projectId) return;
    try {
      const idToken = await user.getIdToken(true);
      await fetch(
        `${API_BASE_URL}/api/projects/${encodeURIComponent(projectId)}/shop-items/detect`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({ forceRefresh: true }),
        },
      );
      setPrepStatus('processing');
      setError('');
      loadShopItems({ initial: false });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  const renderedWidth = imgRect.width;
  const renderedHeight = imgRect.height;
  const selectedOverlayStyle =
    selectedItem && renderedWidth > 0 && renderedHeight > 0
      ? bboxOverlayStyle(selectedItem.item, renderedWidth, renderedHeight)
      : null;

  const rowStyle = {
    display: 'flex',
    flexDirection: isDesktopLayout ? 'row' : 'column',
    alignItems: 'flex-start',
    gap: isDesktopLayout ? 20 : 16,
    width: '100%',
  };

  const panelStyle = {
    flex: isDesktopLayout ? '0 0 320px' : '1 1 auto',
    width: isDesktopLayout ? 320 : '100%',
    maxHeight: isDesktopLayout ? 'min(80vh, 720px)' : undefined,
    overflowY: 'auto',
  };
  return (
    <div className="shop-furniture-panel">
      <div
        className="shop-furniture-panel-header"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 12,
        }}
      >
        <h2
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            margin: 0,
            fontSize: '1.25rem',
          }}
        >
          <ShoppingCart size={22} strokeWidth={2} aria-hidden />
          <span className="shop-furniture-panel-header-title">Shop the furniture</span>
        </h2>
        <button
          type="button"
          className="btn btn-ghost shop-furniture-close-btn"
          aria-label="Close shop panel"
          onClick={onClose}
          style={{ padding: 6, minWidth: 0 }}
        >
          <X size={20} strokeWidth={2} aria-hidden />
        </button>
      </div>

      {prepStatus === 'processing' && !loading ? (
        <p>Preparing furniture items&hellip;</p>
      ) : null}
      {error ? (
        <p className="dashboard-inline-error" role="alert">
          {error}
          {adminUser && prepStatus === 'failed' ? (
            <>
              {' '}
              <button type="button" className="btn btn-outline" onClick={forceRefreshPrep}>
                Retry preparation
              </button>
            </>
          ) : null}
        </p>
      ) : null}

      <div style={rowStyle}>
        <div style={{ flex: '1 1 auto', minWidth: 0 }}>
          {generatedImageUrl ? (
            <div style={IMAGE_WRAPPER_STYLE}>
              <img
                ref={imgRef}
                src={generatedImageUrl}
                alt="Generated design"
                style={IMAGE_STYLE}
                onLoad={measure}
              />
              {renderedWidth > 0 && renderedHeight > 0 && prepStatus === 'ready'
                ? items.map((item, idx) => {
                    const pos = dotPosition(item, renderedWidth, renderedHeight);
                    if (!pos || !Number.isFinite(pos.left) || !Number.isFinite(pos.top)) {
                      return null;
                    }
                    const key = itemKey(item, idx);
                    const sel =
                      selectedItem && isSameSelection(selectedItem, { item, index: idx });
                    const labelText =
                      (typeof item.label === 'string' && item.label) ||
                      (typeof item.category === 'string' && item.category) ||
                      'item';
                    return (
                      <button
                        key={key}
                        type="button"
                        className="shop-furniture-hotspot"
                        aria-label={`Select ${labelText}`}
                        style={{
                          left: pos.left,
                          top: pos.top,
                          zIndex: sel ? 3 : 2,
                        }}
                        onClick={() =>
                          setSelectedItem((prev) =>
                            prev && isSameSelection(prev, { item, index: idx })
                              ? prev
                              : { item, index: idx },
                          )
                        }
                      >
                        <span
                          className={`ai-target-dot${sel ? ' ai-target-dot--selected' : ''}`}
                          aria-hidden
                        />
                      </button>
                    );
                  })
                : null}
              {selectedOverlayStyle ? (
                <div
                  className="shop-furniture-selected-bbox"
                  style={selectedOverlayStyle}
                  aria-hidden
                />
              ) : null}
            </div>
          ) : null}
        </div>

        <div style={panelStyle} className="shop-furniture-panel-side">
          <div className="card shop-furniture-controls-card">
            <label className="shop-furniture-item-country">
              <span className="shop-furniture-item-country-label">
                <MapPin className="shop-furniture-item-country-icon" strokeWidth={2} aria-hidden />
                {shoppingLocationLabel}
              </span>
              <select
                className="form-control"
                value={selectedCountry}
                onChange={(e) => {
                  const nextCountry = normalizeShoppingCountry(e.target.value) || 'ae';
                  setSelectedShoppingCountry(nextCountry);
                  setShoppingCountrySource('manual');
                  setProductSearch(null);
                  setProductSearchError('');
                  try {
                    localStorage.setItem(SHOP_COUNTRY_STORAGE_KEY, nextCountry);
                    localStorage.setItem(SHOP_COUNTRY_MANUAL_STORAGE_KEY, '1');
                  } catch {
                    /* ignore */
                  }
                  logLocationDebug('[ShopUI] selected country', {
                    country: nextCountry,
                    countryName: countryLabel(nextCountry),
                  });
                  logLocationDebug('[ShopUI] location unlocked true/false', {
                    country: nextCountry,
                    unlocked: locationUnlocks[nextCountry]?.unlocked === true,
                  });
                }}
              >
                {COUNTRY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>

            {selectedCountryUnlocked ? (
              <p className="shop-furniture-products-status" style={{ margin: '10px 0 0' }}>
                Furniture search unlocked for {selectedCountryName}
              </p>
            ) : (
              <button
                type="button"
                className="btn btn-primary shop-furniture-search-btn shop-furniture-unlock-btn"
                disabled={unlockLoading}
                onClick={unlockFurnitureSearch}
              >
                <Unlock size={18} strokeWidth={2} aria-hidden />
                {unlockLoading ? 'Unlocking...' : 'Unlock Furniture Search'}
              </button>
            )}

            {!selectedCountryUnlocked ? (
            <div className="shop-furniture-credit-wrap" aria-live="polite" aria-atomic="true">
              <button
                type="button"
                className="new-design-image-quality-credit-line shop-furniture-credit-line"
                data-free={showFreeTrialCopy ? 'true' : undefined}
                disabled
                aria-label={
                  showFreeTrialCopy ? 'Free furniture search trial' : `Cost ${PRODUCT_SEARCH_CREDIT_COST} credits`
                }
              >
                {showFreeTrialCopy ? (
                  'FREE'
                ) : (
                  <>
                    Cost {PRODUCT_SEARCH_CREDIT_COST}{' '}
                    <Database size={14} strokeWidth={2} aria-hidden />
                  </>
                )}
              </button>
            </div>
            ) : null}

            {showFreeTrialCopy ? (
              <p className="shop-furniture-trial-copy">
                Free Trial: 1 project, up to 5 item searches.
              </p>
            ) : null}
            <p className="shop-furniture-save-copy">
              {selectedCountryUnlocked
                ? `Search results are saved for ${selectedCountryName}.`
                : `Furniture search is locked for ${selectedCountryName}.`}
            </p>
            {!selectedCountryUnlocked && selectedItemId ? (
              <p className="shop-furniture-save-copy">
                Search results for {selectedCountryName} are locked. Unlock this location to find matching products there.
              </p>
            ) : null}

            {productSearchError ? (
              <p className="dashboard-inline-error" role="alert" style={{ margin: '12px 0 0' }}>
                {productSearchError}
                {productSearchError.includes('buy credits') ? (
                  <>
                    {' '}
                    <Link to="/pricing">View pricing</Link>
                  </>
                ) : null}
              </p>
            ) : null}
          </div>

          <div className="card shop-furniture-items-card">
            <h3 className="shop-furniture-items-title">Detected items</h3>
            {items.length > 0 ? (
              items.map((item, idx) => {
                const itemId = itemIdOf(item);
                const itemCountry = selectedCountry;
                const itemCountryName = countryLabel(itemCountry);
                const itemCountryShortName = countryShortLabel(itemCountry);
                const resultKey = productCacheKey(itemId, itemCountry);
                const savedResult = itemSearchResults[resultKey];
                const cachedForItem = savedResult?.status === 'ready' ? savedResult : null;
                const activeProductSearch =
                  productSearch?.itemId === itemId && productSearch?.country === itemCountry
                    ? productSearch
                    : cachedForItem;
                const products = Array.isArray(activeProductSearch?.products)
                  ? activeProductSearch.products
                  : [];
                const searchLocked = !selectedCountryUnlocked;
                const isSearching = itemSearchLoadingKey === resultKey;
                const selected = selectedItem && itemIdOf(selectedItem.item) === itemId;
                return (
                  <div
                    key={itemKey(item, idx)}
                    className={`shop-furniture-item-card${
                      selected ? ' shop-furniture-item-card--selected' : ''
                    }`}
                  >
                    <button
                      type="button"
                      className="shop-furniture-item-card-main"
                      onClick={() => setSelectedItem({ item, index: idx })}
                    >
                      <span className="shop-furniture-item-thumb">
                        {itemThumbUrl(item) ? (
                          <img src={itemThumbUrl(item)} alt="" loading="lazy" decoding="async" />
                        ) : null}
                      </span>
                      <span className="shop-furniture-item-copy">
                        <strong>{itemLabel(item)}</strong>
                        {searchLocked ? (
                          <small>Locked for {itemCountryName}</small>
                        ) : cachedForItem ? (
                          <small>Saved results for {itemCountryName}</small>
                        ) : (
                          <small>Ready to search in {itemCountryName}</small>
                        )}
                      </span>
                    </button>
                    <button
                      type="button"
                      className={`project-result-btn project-result-btn--secondary shop-furniture-search-btn${
                        searchLocked ? ' shop-furniture-search-btn--locked' : ''
                      }`}
                      disabled={searchLocked || Boolean(itemSearchLoadingKey)}
                      onClick={() => searchProductsFor({ item, index: idx })}
                    >
                      <Search size={17} strokeWidth={2} aria-hidden />
                      {searchLocked
                        ? `Locked for ${itemCountryShortName}`
                        : isSearching
                          ? 'Searching...'
                          : 'Find Similar Item'}
                    </button>
                    {searchLocked ? (
                      <p className="shop-furniture-products-status">
                        Search results for {itemCountryName} are locked. Unlock this location to find matching products there.
                      </p>
                    ) : null}
                    {isSearching ? (
                      <p className="shop-furniture-products-status">Searching...</p>
                    ) : activeProductSearch ? (
                      <div className="shop-furniture-products-results">
                        <h4 className="shop-furniture-products-title">Search results</h4>
                        {products.length > 0 ? (
                          <div className="shop-furniture-products-list">
                            {products.map((product, productIdx) => {
                              const link =
                                typeof product?.link === 'string' ? product.link.trim() : '';
                              const thumb =
                                typeof product?.thumbnail === 'string'
                                  ? product.thumbnail.trim()
                                  : '';
                              const card = (
                                <>
                                  <span className="shop-furniture-product-thumb">
                                    {thumb ? (
                                      <img src={thumb} alt="" loading="lazy" decoding="async" />
                                    ) : null}
                                  </span>
                                  <span className="shop-furniture-product-copy">
                                    <strong>{productTitle(product)}</strong>
                                    {productMeta(product) ? (
                                      <small>{productMeta(product)}</small>
                                    ) : null}
                                  </span>
                                </>
                              );
                              return link ? (
                                <a
                                  key={`${link}-${productIdx}`}
                                  href={link}
                                  className="shop-furniture-product-card"
                                  target="_blank"
                                  rel="noreferrer"
                                  onClick={() => {
                                    trackEvent('store_link_clicked', {
                                      item_name: itemLabel(item),
                                      store_name:
                                        typeof product?.source === 'string'
                                          ? product.source
                                          : undefined,
                                      selectedShoppingCountry: itemCountry,
                                      project_id: projectId,
                                    });
                                  }}
                                >
                                  {card}
                                </a>
                              ) : (
                                <div
                                  key={`product-${productIdx}`}
                                  className="shop-furniture-product-card"
                                >
                                  {card}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="shop-furniture-products-status">
                            No similar products found yet.
                          </p>
                        )}
                      </div>
                    ) : null}
                  </div>
                );
              })
            ) : (
              <p style={{ margin: 0, color: '#555', fontSize: 14 }}>
                Select an item on the image to search for similar furniture.
              </p>
            )}
          </div>
        </div>
      </div>

      {!loading && prepStatus === 'ready' && items.length === 0 ? (
        <p style={{ marginTop: 12, color: '#555' }}>No furniture items were detected in this design.</p>
      ) : null}
    </div>
  );
}

export default ShopFurniturePanel;